// 統合されたUIコンポーネント
import React, { forwardRef, memo, useMemo } from 'react';
import {
  GENDERS,
  OCCUPATIONS,
  PREFECTURES,
  RECRUITMENT_TAGS,
  SUBJECTS,
  TEACHING_METHODS,
  TUTOR_TAGS,
} from '../libs/constants';
import type {
  ButtonProps,
  CardProps,
  ModalProps,
  PaginationProps,
  Recruitment,
  Tutor,
} from '../types';

// ボタンコンポーネント
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  children,
  className = '',
  ariaLabel,
  ariaDescribedby,
}) => {
  const baseClasses =
    'btn focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200';
  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
  };
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      type="button"
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} ${
        disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      aria-busy={loading}
    >
      {loading ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          処理中...
        </div>
      ) : (
        children
      )}
    </button>
  );
};

// カードコンポーネント
export const Card: React.FC<CardProps> = ({ className = '', children, onClick }) => {
  return (
    <div
      className={`card ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
};

// ページネーションコンポーネント
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages = pages.filter(
    (page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2,
  );

  return (
    <div className="flex justify-center items-center space-x-2 mt-8">
      <Button
        variant="outline"
        size="sm"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        前へ
      </Button>

      {visiblePages.map((page, index) => (
        <React.Fragment key={page}>
          {index > 0 &&
            visiblePages[index - 1] !== undefined &&
            (visiblePages[index - 1] as number) < page - 1 && (
              <span className="text-gray-400">...</span>
            )}
          <button
            type="button"
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded text-sm ${
              page === currentPage ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        </React.Fragment>
      ))}

      <Button
        variant="outline"
        size="sm"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        次へ
      </Button>
    </div>
  );
};

// モーダルコンポーネント
export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  React.useEffect(() => {
    if (isOpen) {
      // Trap focus
      const previouslyFocused = document.activeElement as HTMLElement;
      const modal = document.getElementById('modal-content');
      modal?.focus();

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        previouslyFocused?.focus();
      };
    }

    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <button
          type="button"
          className="fixed inset-0 bg-black bg-opacity-25"
          onClick={onClose}
          aria-label="Close modal"
        ></button>
        <div
          id="modal-content"
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabIndex={-1}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 id="modal-title" className="text-lg font-medium">
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="閉じる"
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// ローディングスピナー
export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; label?: string }> = ({
  size = 'md',
  label = '読み込み中',
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <output className="flex justify-center items-center">
      <div
        className={`animate-spin rounded-full border-2 border-blue-600 border-t-transparent ${sizeClasses[size]}`}
        aria-hidden="true"
      ></div>
      <span className="sr-only">{label}</span>
    </output>
  );
};

// エラー表示
export const ErrorMessage: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div
      className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700"
      role="alert"
      aria-live="assertive"
    >
      <p className="font-medium">エラーが発生しました</p>
      <p className="text-sm mt-1">{message}</p>
    </div>
  );
};

// 空状態表示
export const EmptyState: React.FC<{ message: string; action?: React.ReactNode }> = ({
  message,
  action,
}) => {
  return (
    <div className="text-center py-12">
      <div className="text-gray-400 text-6xl mb-4">📝</div>
      <p className="text-gray-600 mb-4">{message}</p>
      {action && <div>{action}</div>}
    </div>
  );
};

// ===================
// フォームコンポーネント
// ===================

interface FormFieldProps {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  htmlFor?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  required,
  children,
  htmlFor,
}) => (
  <div className="space-y-1">
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && <p className="text-sm text-red-600">{error}</p>}
  </div>
);

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ label, error, required, className = '', id, ...props }, ref) => (
    <FormField label={label} error={error} required={required} htmlFor={id}>
      <input
        ref={ref}
        id={id}
        className={`form-field ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
        {...props}
      />
    </FormField>
  ),
);

FormInput.displayName = 'FormInput';

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ label, error, required, className = '', id, ...props }, ref) => (
    <FormField label={label} error={error} required={required} htmlFor={id}>
      <textarea
        ref={ref}
        id={id}
        className={`form-field ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
        rows={4}
        {...props}
      />
    </FormField>
  ),
);

FormTextarea.displayName = 'FormTextarea';

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
  options: readonly string[] | string[];
  placeholder?: string;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    { label, error, required, options, placeholder = 'すべて', className = '', id, ...props },
    ref,
  ) => (
    <FormField label={label} error={error} required={required} htmlFor={id}>
      <select
        ref={ref}
        id={id}
        className={`form-field ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </FormField>
  ),
);

FormSelect.displayName = 'FormSelect';

interface FormMultiSelectProps {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
  options: readonly string[] | string[];
  value: string[];
  onChange: (value: string[]) => void;
}

export const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  label,
  error,
  required,
  options,
  value,
  onChange,
}) => {
  const handleChange = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <FormField label={label} error={error} required={required}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border border-gray-300 rounded-md bg-white">
        {options.map((option) => (
          <label key={option} className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={value.includes(option)}
              onChange={() => handleChange(option)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </FormField>
  );
};

// プリセット選択コンポーネント
interface PresetSelectProps extends Omit<FormSelectProps, 'options'> {
  type: 'prefecture' | 'subject' | 'teaching_method' | 'gender' | 'occupation';
}

export const PresetSelect = forwardRef<HTMLSelectElement, PresetSelectProps>(
  ({ type, ...props }, ref) => {
    const optionsMap = {
      prefecture: PREFECTURES,
      subject: SUBJECTS,
      teaching_method: TEACHING_METHODS,
      gender: GENDERS,
      occupation: OCCUPATIONS,
    };

    return <FormSelect ref={ref} options={optionsMap[type]} {...props} />;
  },
);

PresetSelect.displayName = 'PresetSelect';

interface PresetMultiSelectProps extends Omit<FormMultiSelectProps, 'options'> {
  type: 'subject';
}

export const PresetMultiSelect: React.FC<PresetMultiSelectProps> = ({ type, ...props }) => {
  const optionsMap = {
    subject: SUBJECTS,
  };

  return <FormMultiSelect options={optionsMap[type]} {...props} />;
};

// =========================
// 最適化されたコンポーネント
// =========================

// メモ化されたタグフィルター
export const MemoizedTagFilter: React.FC<{
  type: 'tutor' | 'recruitment';
  selectedTag: string;
  onTagChange: (tag: string) => void;
}> = memo(
  ({ type, selectedTag, onTagChange }) => {
    const flatTags = useMemo(() => {
      return Object.values(type === 'tutor' ? TUTOR_TAGS : RECRUITMENT_TAGS).flat();
    }, [type]);

    return (
      <select
        value={selectedTag}
        onChange={(e) => onTagChange(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded-md"
      >
        <option value="">すべて</option>
        {flatTags.map((tag: string) => (
          <option key={tag} value={tag}>
            {tag}
          </option>
        ))}
      </select>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.selectedTag === nextProps.selectedTag &&
      prevProps.onTagChange === nextProps.onTagChange
    );
  },
);

MemoizedTagFilter.displayName = 'MemoizedTagFilter';

// メモ化された家庭教師カード
export const MemoizedTutorCard: React.FC<{ tutor: Tutor }> = memo(
  ({ tutor }) => {
    const displayTags = useMemo(() => {
      return tutor.tags.slice(0, 3);
    }, [tutor.tags]);

    const rateDisplay = useMemo(() => {
      return `¥${tutor.hourlyRate.toLocaleString()}/時間`;
    }, [tutor.hourlyRate]);

    return (
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center space-x-3 mb-3">
          {tutor.imageUrl && (
            <img
              src={tutor.imageUrl}
              alt={tutor.nickname}
              className="w-12 h-12 rounded-full object-cover"
              loading="lazy"
            />
          )}
          <div>
            <h3 className="font-semibold text-lg">{tutor.nickname}</h3>
            <p className="text-gray-600">{tutor.prefecture}</p>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-3 line-clamp-2">{tutor.introduction}</p>

        <div className="flex flex-wrap gap-1 mb-3">
          {displayTags.map((tag) => (
            <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
              {tag}
            </span>
          ))}
          {tutor.tags.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
              +{tutor.tags.length - 3}
            </span>
          )}
        </div>

        <div className="flex justify-between items-center">
          <span className="font-semibold text-green-600">{rateDisplay}</span>
          <span className="text-sm text-gray-500">{tutor.teachingMethod}</span>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.tutor.id === nextProps.tutor.id &&
      prevProps.tutor.updatedAt === nextProps.tutor.updatedAt
    );
  },
);

MemoizedTutorCard.displayName = 'MemoizedTutorCard';

// メモ化された募集カード
export const MemoizedRecruitmentCard: React.FC<{ recruitment: Recruitment }> = memo(
  ({ recruitment }) => {
    const displaySubjects = useMemo(() => {
      return recruitment.subjects.slice(0, 3);
    }, [recruitment.subjects]);

    const displayTags = useMemo(() => {
      return recruitment.tags.slice(0, 2);
    }, [recruitment.tags]);

    return (
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-lg flex-1">{recruitment.title}</h3>
          <span className="text-sm text-gray-500 ml-2">{recruitment.prefecture}</span>
        </div>

        <p className="text-sm text-gray-700 mb-3 line-clamp-2">{recruitment.description}</p>

        <div className="flex flex-wrap gap-1 mb-2">
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">学生</span>
          {displaySubjects.map((subject) => (
            <span
              key={subject}
              className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
            >
              {subject}
            </span>
          ))}
          {recruitment.subjects.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
              +{recruitment.subjects.length - 3}
            </span>
          )}
        </div>

        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {displayTags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
              >
                {tag}
              </span>
            ))}
            {recruitment.tags.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                +{recruitment.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.recruitment.id === nextProps.recruitment.id &&
      prevProps.recruitment.updatedAt === nextProps.recruitment.updatedAt
    );
  },
);

MemoizedRecruitmentCard.displayName = 'MemoizedRecruitmentCard';
